import React, { useState } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Typography, Space, Badge, Tag, Modal, message } from 'antd';
import {
  DashboardOutlined,
  TruckOutlined,
  UserOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  CarOutlined,
  TeamOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '../services/notificationService';
import type { Notification } from '../services/notificationService';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [notificationModalVisible, setNotificationModalVisible] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Gestion du profil utilisateur
  const handleProfileNavigation = () => {
    navigate('/admin/profile');
  };

  // Gestion des paramètres de l'entreprise
  const handleCompanySettingsNavigation = () => {
    navigate('/admin/settings/company');
  };

  // Gestion des utilisateurs
  const handleUsersManagement = () => {
    navigate('/admin/settings/users');
  };

  // Gestion des notifications
  const handleNotifications = () => {
    setNotificationModalVisible(true);
  };

  const queryClient = useQueryClient();

  // Récupérer les notifications avec polling (30s)
  const { data: notificationsData } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationService.getNotifications({ limit: 50 }),
    refetchInterval: 30000, // Polling toutes les 30 secondes
  });

  // Récupérer le nombre de notifications non lues avec polling (30s)
  const { data: unreadCountData } = useQuery({
    queryKey: ['notificationsUnreadCount'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000, // Polling toutes les 30 secondes
  });

  // Mutation pour marquer toutes les notifications comme lues
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: (response: { message: string }) => {
      message.success(response.message);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationsUnreadCount'] });
      setNotificationModalVisible(false);
    },
    onError: () => {
      message.error('Erreur lors de la mise à jour des notifications');
    },
  });

  // Marquer les notifications comme lues
  const markAllNotificationsAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const notifications = notificationsData?.data || [];
  const unreadNotificationsCount = unreadCountData?.unread_count || 0;

  // Confirmer la déconnexion
  const handleSignOut = () => {
    Modal.confirm({
      title: 'Confirmation de déconnexion',
      icon: <ExclamationCircleOutlined />,
      content: 'Êtes-vous sûr de vouloir vous déconnecter ?',
      okText: 'Oui, me déconnecter',
      cancelText: 'Annuler',
      onOk: signOut,
    });
  };

  // Éléments du menu principal
  const menuItems = [
    {
      key: '/admin',
      icon: <DashboardOutlined />,
      label: 'Tableau de bord',
      onClick: () => navigate('/admin'),
    },
    {
      key: '/admin/vehicles',
      icon: <CarOutlined />,
      label: 'Véhicules',
      onClick: () => navigate('/admin/vehicles'),
    },
    {
      key: '/admin/drivers',
      icon: <UserOutlined />,
      label: 'Chauffeurs',
      onClick: () => navigate('/admin/drivers'),
    },
    {
      key: '/admin/missions',
      icon: <FileTextOutlined />,
      label: 'Missions',
      onClick: () => navigate('/admin/missions'),
    },
    {
      key: 'fleet',
      icon: <TruckOutlined />,
      label: 'Gestion Flotte',
      children: [
        {
          key: '/admin/fleet/maintenance',
          label: 'Maintenance',
          onClick: () => navigate('/admin/fleet/maintenance'),
        },
        {
          key: '/admin/fleet/fuel',
          label: 'Carburant',
          onClick: () => navigate('/admin/fleet/fuel'),
        },
        {
          key: '/admin/fleet/tracking',
          label: 'Suivi GPS',
          onClick: () => navigate('/admin/fleet/tracking'),
        },
      ],
    },
    {
      key: 'reports',
      icon: <FileTextOutlined />,
      label: 'Rapports',
      children: [
        {
          key: '/admin/reports/missions',
          label: 'Rapport missions',
          onClick: () => navigate('/admin/reports/missions'),
        },
        {
          key: '/admin/reports/performance',
          label: 'Performance',
          onClick: () => navigate('/admin/reports/performance'),
        },
        {
          key: '/admin/reports/costs',
          label: 'Coûts',
          onClick: () => navigate('/admin/reports/costs'),
        },
      ],
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Configuration',
      children: [
        {
          key: '/admin/settings/company',
          label: 'Entreprise',
          onClick: handleCompanySettingsNavigation,
        },
        {
          key: '/admin/settings/mission-types',
          label: 'Types de missions',
          onClick: () => navigate('/admin/settings/mission-types'),
        },
        {
          key: '/admin/settings/users',
          label: 'Utilisateurs',
          onClick: handleUsersManagement,
        },
      ],
    },
  ];

  // Menu du profil utilisateur
  const profileMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Mon profil',
      onClick: handleProfileNavigation,
    },
    {
      key: 'company-settings',
      icon: <SettingOutlined />,
      label: 'Paramètres entreprise',
      onClick: handleCompanySettingsNavigation,
    },
    {
      key: 'user-management',
      icon: <TeamOutlined />,
      label: 'Gestion utilisateurs',
      onClick: handleUsersManagement,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Déconnexion',
      onClick: handleSignOut,
      danger: true,
    },
  ];

  // Déterminer l'élément de menu sélectionné
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/admin') return ['/admin'];
    if (path.startsWith('/admin/vehicles')) return ['/admin/vehicles'];
    if (path.startsWith('/admin/drivers')) return ['/admin/drivers'];
    if (path.startsWith('/admin/missions')) return ['/admin/missions'];
    if (path.startsWith('/admin/fleet')) return [path];
    if (path.startsWith('/admin/reports')) return [path];
    if (path.startsWith('/admin/settings')) return [path];
    return [path];
  };

  // Obtenir la couleur du statut de l'entreprise
  const getTenantStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'suspended': return 'orange';
      case 'inactive': return 'red';
      default: return 'default';
    }
  };

  // Obtenir le texte du statut de l'entreprise
  const getTenantStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Actif';
      case 'suspended': return 'Suspendu';
      case 'inactive': return 'Inactif';
      default: return status;
    }
  };

  // Formater le temps depuis la création
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'À l\'instant';
    if (seconds < 3600) return `Il y a ${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `Il y a ${Math.floor(seconds / 3600)} h`;
    if (seconds < 604800) return `Il y a ${Math.floor(seconds / 86400)} j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          background: '#001529',
        }}
      >
        {/* Logo et nom de l'entreprise */}
        <div style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          margin: '16px',
          borderRadius: '6px',
          color: '#fff',
        }}>
          <TruckOutlined style={{ fontSize: '24px', marginRight: collapsed ? 0 : '8px' }} />
          {!collapsed && (
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              FleetBase
            </span>
          )}
        </div>

        {/* Informations de l'entreprise */}
        {!collapsed && user?.tenant && (
          <div style={{
            padding: '8px 16px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            marginBottom: '8px',
          }}>
            <div style={{ color: '#fff', fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
              {user.tenant.name}
            </div>
            <Tag 
              color={getTenantStatusColor(user.tenant.status)}
              style={{ margin: 0 }}
            >
              {getTenantStatusText(user.tenant.status)}
            </Tag>
          </div>
        )}

        {/* Menu principal */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKey()}
          style={{ borderRight: 0 }}
          items={menuItems}
        />

        {/* Badge Admin */}
        {!collapsed && (
          <div style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '8px',
            borderRadius: '4px',
            textAlign: 'center',
          }}>
            <Badge 
              status="processing" 
              text={
                <Text style={{ color: '#fff', fontSize: '12px' }}>
                  Administrateur
                </Text>
              } 
            />
          </div>
        )}
      </Sider>

      {/* Layout principal */}
      <Layout>
        {/* Header */}
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          {/* Bouton collapse et titre */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
            <div style={{ lineHeight: 0 }}>
              <Text strong style={{ fontSize: '16px' }}>
                {user?.tenant?.name || 'Gestion de Flotte'}
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Administration de votre entreprise
                </Text>
              </div>
            </div>
          </div>

          {/* Actions utilisateur */}
          <Space size="middle">
            {/* Statut de l'entreprise */}
            {user?.tenant && (
              <Tag 
                color={getTenantStatusColor(user.tenant.status)}
                style={{ margin: 0 }}
              >
                {getTenantStatusText(user.tenant.status)}
              </Tag>
            )}

            {/* Notifications */}
            <Badge count={unreadNotificationsCount} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                style={{ fontSize: '16px' }}
                onClick={handleNotifications}
              />
            </Badge>

            {/* Profil utilisateur */}
            <Dropdown
              menu={{ items: profileMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.3s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />}
                  style={{ marginRight: '8px', height: '38px', width:'38px', fontSize: '24px' }}
                />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: '16px' }}>
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', lineHeight: '24px' }}>
                    Administrateur
                  </div>
                </div>
              </div>
            </Dropdown>
          </Space>
        </Header>

        {/* Contenu principal */}
        <Content style={{
          margin: 0,
          background: '#f5f5f5',
          minHeight: 'calc(100vh - 64px)',
          overflow: 'auto',
        }}>
          {children}
        </Content>
      </Layout>

      {/* Modal des notifications */}
      <Modal
        title="Notifications"
        open={notificationModalVisible}
        onCancel={() => setNotificationModalVisible(false)}
        footer={[
          <Button key="mark-read" onClick={markAllNotificationsAsRead}>
            Marquer tout comme lu
          </Button>,
          <Button key="close" type="primary" onClick={() => setNotificationModalVisible(false)}>
            Fermer
          </Button>,
        ]}
        width={600}
      >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {notifications.length > 0 ? (
            notifications.map((notif: Notification) => (
              <div
                key={notif.id}
                style={{
                  padding: '12px',
                  borderBottom: '1px solid #f0f0f0',
                  backgroundColor: notif.is_read ? '#fff' : '#f6ffed',
                  borderRadius: '4px',
                  marginBottom: '8px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: notif.is_read ? 'normal' : 'bold',
                      fontSize: '14px',
                      marginBottom: '4px'
                    }}>
                      {notif.title}
                    </div>
                    <div style={{
                      color: '#666',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      marginBottom: '8px'
                    }}>
                      {notif.message}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999' }}>
                      {formatTimeAgo(notif.created_at)}
                    </div>
                  </div>
                  {!notif.is_read && (
                    <Badge
                      status="processing"
                      style={{ marginLeft: '8px' }}
                    />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <BellOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>Aucune notification</div>
            </div>
          )}
        </div>
      </Modal>
    </Layout>
  );
};

export default AdminLayout;